import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import { DrawerBody } from '@growtherapy/sprout-ui';

import { useMarkFormResponseAsReviewed } from '@grow-therapy-team/seedling-components';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { ProviderDrawerState, drawerStateAtom } from '../state';
import { ClientInformationDrawerHeader } from './ClientInformationDrawerHeader';
import { FormResponse } from './FormResponse';
import { FormListItem } from './form-list-item';
import { selectedFormTitleAtom, selectedGroupedFormAtom } from './state';
import { FormType, GroupedFormData } from './types';

function useMarkIntakeFormsAsReviewed(
  selectedForms: GroupedFormData[] | undefined,
  formEntry: number,
  revealResults?: boolean,
) {
  const { markFormResponseAsReviewed } = useMarkFormResponseAsReviewed();
  const markedResponseIds = useRef<Set<string>>(new Set());

  useEffect(
    function markIntakeFormAsReviewedOnView() {
      const selectedForm = selectedForms?.[formEntry];
      const responseId = selectedForm?.formResponseId;
      if (!revealResults || selectedForm?.formType !== FormType.Intake || !responseId) return;
      if (markedResponseIds.current.has(responseId)) return;
      markedResponseIds.current.add(responseId);
      markFormResponseAsReviewed({ formResponseId: parseInt(responseId) });
    },
    [revealResults, formEntry, selectedForms, markFormResponseAsReviewed],
  );

  const { userViewed } = useTrackEvent();

  useEffect(
    function trackIntakeFormReviewDuration() {
      const selectedForm = selectedForms?.[formEntry];
      const responseId = selectedForm?.formResponseId;
      if (!revealResults || selectedForm?.formType !== FormType.Intake || !responseId) return;
      const startTime = Date.now();
      // return a cleanup function to fire the event when the drawer closes
      return () => {
        const duration = Date.now() - startTime;
        // ignore sub-decisecond durations (e.g. caused by effect re-runs from unrelated upstream re-renders a few ms after initial page load)
        if (duration < 100) return;
        userViewed(EventTypeEnum.INTAKE_FORM_RESPONSE_REVIEWED, {
          formResponseId: responseId,
          duration,
        });
      };
    },
    [revealResults, formEntry, selectedForms, userViewed],
  );
}

type ClientInformationFormResponseDrawerProps = {
  selectedForms: GroupedFormData[];
  revealResults?: boolean;
  selectedFormTitle: string;
  onClickForm: (i: number) => void;
  onClickBackButton: () => void;
  formEntry: number;
};

export function ClientInformationFormResponseDrawer({
  selectedForms,
  selectedFormTitle,
  formEntry,
  onClickBackButton,
  onClickForm,
  revealResults,
}: ClientInformationFormResponseDrawerProps) {
  const drawerTitle =
    selectedForms[0].formType === FormType.Gad7 || selectedForms[0].formType === FormType.Phq9
      ? `${selectedForms[0].formType} - ${selectedFormTitle}`
      : selectedFormTitle;

  return (
    <>
      <ClientInformationDrawerHeader drawerTitle={drawerTitle} onClickBackButton={onClickBackButton} />
      <DrawerBody>
        {!revealResults ? (
          <span data-testid='client-information.forms.form-list'>
            {selectedForms.map((form, i) => (
              <FormListItem
                form={form}
                key={`${form.formType}-${i}`}
                onClick={() => {
                  onClickForm(i);
                }}
              />
            ))}
          </span>
        ) : (
          <FormResponse selectedForm={selectedForms[formEntry]} />
        )}
      </DrawerBody>
    </>
  );
}

export function ClientInformationFormResponseDrawerWrapper() {
  const selectedFormTitle = useAtomValue(selectedFormTitleAtom);
  const selectedFormResponses = useAtomValue(selectedGroupedFormAtom);
  const hasSingleSubmission = selectedFormResponses?.length === 1 ? true : false;
  const setDrawerState = useSetAtom(drawerStateAtom);
  const [revealResults, setRevealResults] = useState(hasSingleSubmission);
  const [formEntry, setFormEntry] = useState(0);

  useMarkIntakeFormsAsReviewed(selectedFormResponses, formEntry, revealResults);

  const onClickBackButton = () => {
    if (revealResults && !hasSingleSubmission) {
      setRevealResults(!revealResults);
    } else {
      setDrawerState(ProviderDrawerState.CLIENT_INFORMATION);
    }
  };

  const onClickForm = (i: number) => {
    setFormEntry(i);
    setRevealResults(!revealResults);
  };

  if (!selectedFormTitle || !selectedFormResponses) return;

  return (
    <ClientInformationFormResponseDrawer
      selectedFormTitle={selectedFormTitle}
      revealResults={revealResults}
      selectedForms={selectedFormResponses}
      onClickBackButton={onClickBackButton}
      onClickForm={onClickForm}
      formEntry={formEntry}
    />
  );
}
